import React from 'react';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Avatar from '../ui/Avatar';
import { Teacher, Class } from '../../data/types';
import PageContainer from '../layouts/PageContainer';

interface TeacherDashboardScreenProps {
  teacher: Teacher;
  classes: Class[];
}

const TeacherDashboardScreen: React.FC<TeacherDashboardScreenProps> = ({ 
  teacher,
  classes
}) => {
  // Get current date
  const today = new Date();
  const dateOptions: Intl.DateTimeFormatOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
  const formattedDate = today.toLocaleDateString('en-US', dateOptions);

  return (
    <PageContainer title={`Welcome back, ${teacher.firstName}!`} userRole="Teacher">
      <div className="space-y-6 bg-gray-50">
        <div className="flex justify-between items-center px-1 py-2">
          <p className="text-gray-600">{formattedDate}</p>
          <Button variant="primary" size="sm">
            View Progress Report
          </Button>
        </div>
      
        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <Card className="p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 rounded-md bg-blue-100">
                <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path>
                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"></path>
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-gray-500 text-sm font-medium">Students Active Today</h3>
                <p className="text-2xl font-semibold text-gray-900">28/35</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 rounded-md bg-green-100">
                <svg className="w-6 h-6 text-secondary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M12 1.586l-4 4v12.828l4-4V1.586zM3.707 3.293A1 1 0 002 4v10a1 1 0 00.293.707L6 18.414V5.586L3.707 3.293zM17.707 5.293L14 1.586v12.828l2.293 2.293A1 1 0 0018 16V6a1 1 0 00-.293-.707z" clipRule="evenodd"></path>
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-gray-500 text-sm font-medium">Learning Paths Assigned</h3>
                <p className="text-2xl font-semibold text-gray-900">12</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 rounded-md bg-yellow-100">
                <svg className="w-6 h-6 text-accent" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"></path>
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-gray-500 text-sm font-medium">Standards Needing Attention</h3>
                <p className="text-2xl font-semibold text-gray-900">5</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 rounded-md bg-indigo-100">
                <svg className="w-6 h-6 text-indigo-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z"></path>
                  <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z"></path>
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-gray-500 text-sm font-medium">Overall Class Progress</h3>
                <p className="text-2xl font-semibold text-gray-900">78%</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Two-column layout for main content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column - Top Performing Students and My Classes */}
          <div className="lg:col-span-2 space-y-6">
            {/* Top Performing Students */}
            <Card className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Top Performing Students</h2>
                <Button variant="secondary" size="sm">View All Students</Button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Student
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Class
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Learning Path Progress
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Activity
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {/* Example student rows */}
                    {[1, 2, 3, 4, 5].map((index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Avatar 
                              src={`https://randomuser.me/api/portraits/${index % 2 ? 'women' : 'men'}/${index + 20}.jpg`}
                              size="sm"
                            />
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {['Emily Johnson', 'Michael Smith', 'Sophia Garcia', 'James Wilson', 'Olivia Brown'][index - 1]}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{['Math 5A', 'Math 5B', 'Math 5A', 'Math 5B', 'Math 5A'][index - 1]}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div 
                              className="bg-secondary h-2.5 rounded-full" 
                              style={{ width: `${[95, 88, 82, 76, 70][index - 1]}%` }}
                            ></div>
                          </div>
                          <div className="text-xs font-medium text-gray-900 mt-1">
                            {[95, 88, 82, 76, 70][index - 1]}% Complete
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {['Today', 'Yesterday', '2 days ago', 'Today', 'Yesterday'][index - 1]}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            
            {/* My Classes */}
            <Card className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">My Classes</h2>
                <Button variant="secondary" size="sm">View All</Button>
              </div>
              <div className="space-y-4">
                {classes.map((classItem) => (
                  <div key={classItem.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-lg font-semibold">{classItem.name}</h3>
                        <p className="text-sm text-gray-500">Grade {classItem.grade} • {classItem.students.length} Students</p>
                      </div>
                      <div className="flex items-center">
                        <Badge variant="primary" className="mr-2">
                          {classItem.learningPaths.length} Paths
                        </Badge>
                        <Button variant="tertiary" size="sm">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                          </svg>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
          
          {/* Right column - Recent Activity and To-Do List */}
          <div className="space-y-6">
            {/* Recent Activity */}
            <Card className="p-6">
              <h2 className="text-xl font-bold mb-4">Recent Activity</h2>
              <div className="relative">
                {/* Activity timeline */}
                <div className="border-l-2 border-gray-200 ml-3 space-y-6">
                  <div className="relative group">
                    <div className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full border-2 border-white bg-primary"></div>
                    <time className="mb-1 text-sm font-normal leading-none text-gray-500">Just now</time>
                    <h3 className="text-base font-semibold text-gray-900 group-hover:text-primary transition-colors">
                      New assignment submitted
                    </h3>
                    <p className="mb-2 text-sm font-normal text-gray-600">
                      James Wilson submitted "Fractions Practice"
                    </p>
                    <Button variant="tertiary" size="sm">View Submission</Button>
                  </div>
                  
                  <div className="relative group">
                    <div className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full border-2 border-white bg-secondary"></div>
                    <time className="mb-1 text-sm font-normal leading-none text-gray-500">1 hour ago</time>
                    <h3 className="text-base font-semibold text-gray-900 group-hover:text-primary transition-colors">
                      Learning path completed
                    </h3>
                    <p className="mb-2 text-sm font-normal text-gray-600">
                      Emily Johnson completed "Multiplication Mastery"
                    </p>
                    <Button variant="tertiary" size="sm">View Results</Button>
                  </div>
                  
                  <div className="relative group">
                    <div className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full border-2 border-white bg-accent"></div>
                    <time className="mb-1 text-sm font-normal leading-none text-gray-500">3 hours ago</time>
                    <h3 className="text-base font-semibold text-gray-900 group-hover:text-primary transition-colors">
                      Standards review needed
                    </h3>
                    <p className="mb-2 text-sm font-normal text-gray-600">
                      5 students struggling with "4.NF.A.1" standard
                    </p>
                    <Button variant="tertiary" size="sm">Review Standard</Button>
                  </div>
                </div>
                
                <div className="text-center mt-4">
                  <Button variant="tertiary">View All Activity</Button>
                </div>
              </div>
            </Card>
            
            {/* To-Do List */}
            <Card className="p-6">
              <h2 className="text-xl font-bold mb-4">To-Do List</h2>
              <div className="space-y-3">
                <div className="flex items-center p-2 rounded hover:bg-gray-50">
                  <input 
                    type="checkbox" 
                    className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary"
                  />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Review Math 5A quiz results</p>
                    <p className="text-xs text-gray-500">Due today</p>
                  </div>
                  <Badge variant="error" className="ml-auto">Urgent</Badge>
                </div>
                
                <div className="flex items-center p-2 rounded hover:bg-gray-50">
                  <input 
                    type="checkbox" 
                    className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary"
                  />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Create new measurement unit</p>
                    <p className="text-xs text-gray-500">Due in 2 days</p>
                  </div>
                  <Badge variant="warning" className="ml-auto">Medium</Badge>
                </div>
                
                <div className="flex items-center p-2 rounded hover:bg-gray-50">
                  <input 
                    type="checkbox" 
                    className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary"
                  />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Schedule parent-teacher meetings</p>
                    <p className="text-xs text-gray-500">Due in 5 days</p>
                  </div>
                  <Badge variant="success" className="ml-auto">Low</Badge>
                </div>
                
                <div className="flex items-center p-2 rounded hover:bg-gray-50">
                  <input 
                    type="checkbox" 
                    className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary"
                  />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Submit progress reports</p>
                    <p className="text-xs text-gray-500">Due in 1 week</p>
                  </div>
                  <Badge variant="success" className="ml-auto">Low</Badge>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex">
                  <input
                    type="text"
                    placeholder="Add a new task..."
                    className="block w-full border-0 border-b border-transparent bg-gray-50 focus:border-primary focus:ring-0 sm:text-sm"
                  />
                  <Button variant="primary" size="sm" className="ml-2">Add</Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default TeacherDashboardScreen;
